import { TAB_BAR_HEIGHT } from '../../../../layouts/mobile/MobileTabs';
import { PatientDetailShareWithPatientModal } from './PatientDetailShareWithPatientModal.graphql';
import { PatientDetailToolbar } from './PatientDetailToolbar';
import { PatientDetailPatientCard } from './cards/PatientDetailPatientCard.graphql';
import { PatientDetailScansCard } from './cards/PatientDetailScansCard.graphql';
import { PatientDetailTreatmentCard } from './cards/PatientDetailTreatmentCard.graphql';
import { useQuery } from '@apollo/client';
import type { FragmentType } from '@orthly/graphql-inline-react';
import { graphql, getFragmentData } from '@orthly/graphql-inline-react';
import { styled } from '@orthly/ui-primitives';
import { PracticeBanner } from '@orthly/veneer';
import React from 'react';

const MainLayout = styled('div')(({ theme }) => ({
    gap: '16px',
    padding: '32px 40px',
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
        'patient treatment treatment'
        'scans scans scans'
    `,
    [theme.breakpoints.down('lg')]: {
        padding: `32px 40px ${TAB_BAR_HEIGHT + 32}px 40px`,
    },
    [theme.breakpoints.down('md')]: {
        padding: `16px 16px ${TAB_BAR_HEIGHT + 16}px 16px`,
        gridTemplateAreas: `
            'patient patient patient'
            'treatment treatment treatment'
            'scans scans scans'
        `,
    },
}));

export const PatientDetailMainPatient_Fragment = graphql(`
    fragment PatientDetailMainPatient_Fragment on PatientDTO {
        ...PatientDetailPatientCardPatient_Fragment
        ...PatientDetailShareWithPatientModalPatient_Fragment
        ...PatientScanCardPatient_Fragment
    }
`);

const PatientDetailMainScan_Fragment = graphql(`
    fragment PatientDetailMainScan_Fragment on PatientScan {
        ...PatientDetailTreatmentCardScan_Fragment
        ...PatientDetailScansCardScan_Fragment
    }
`);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PatientDetailMainScanExport_Fragment = graphql(`
    fragment PatientDetailMainScanExport_Fragment on scan_export {
        ...PatientScanCardScanExport_Fragment
        ...OrderDesignPreviewDesignModelPayloadsScanExport_Fragment
    }
`);

const PatientDetailMainScanExport_Query = graphql(`
    query PatientDetailMainScanExport_Query($id: String!) {
        scan_file(scanId: $id) {
            ...PatientDetailMainScanExport_Fragment
        }
    }
`);

const useScanFileData = (scanExportId: string | null) => {
    return useQuery(PatientDetailMainScanExport_Query, {
        variables: { id: scanExportId ?? '' },
        skip: !scanExportId,
        fetchPolicy: 'no-cache',
    });
};

interface PatientDetailMainProps {
    patientMainFragment: FragmentType<typeof PatientDetailMainPatient_Fragment>;
    scanMainFragments: FragmentType<typeof PatientDetailMainScan_Fragment>[];
}

/**
 * Renders the main content on the Patient Detail page (see [storybook](https://storybook-dev.dandyserv.net/main/practice/index.html?path=/story/patients-patient-detail-patientdetailmain--patient-detail-main)).
 */
export const PatientDetailMain: React.VFC<PatientDetailMainProps> = props => {
    const { patientMainFragment, scanMainFragments } = props;

    const [selectedScanIndex, setSelectedScanIndex] = React.useState<number | null>(null);
    const [selectedScanExportId, setSelectedScanExportId] = React.useState<string | null>(null);
    const [shareWithPatientModalOpen, setShareWithPatientModalOpen] = React.useState(false);

    const patientFragment = getFragmentData(PatientDetailMainPatient_Fragment, patientMainFragment);
    const scanFragments = getFragmentData(PatientDetailMainScan_Fragment, scanMainFragments);

    const { data, loading } = useScanFileData(selectedScanExportId);

    return (
        <div>
            <PatientDetailToolbar />
            <PracticeBanner
                banner={{
                    title: 'Scans from orders and Diagnostic Scans appear here post-submission.',
                    type: 'info',
                }}
            />
            <MainLayout>
                <PatientDetailPatientCard patientFragment={patientFragment} />
                <PatientDetailTreatmentCard selectedScanIndex={selectedScanIndex} scanFragments={scanFragments} />
                <PatientDetailScansCard
                    patientFragment={patientFragment}
                    scanFragments={scanFragments}
                    selectedScanExportFragment={data?.scan_file}
                    selectedScanExportLoading={loading}
                    selectedScanIndex={selectedScanIndex}
                    setSelectedScanIndex={setSelectedScanIndex}
                    setSelectedScanExportId={setSelectedScanExportId}
                    setShareWithPatientModalOpen={setShareWithPatientModalOpen}
                />
            </MainLayout>

            <PatientDetailShareWithPatientModal
                patientFragment={patientFragment}
                selectedScanExportFragment={data?.scan_file}
                selectedScanExportId={selectedScanExportId}
                shareWithPatientModalOpen={shareWithPatientModalOpen}
                setShareWithPatientModalOpen={setShareWithPatientModalOpen}
            />
        </div>
    );
};
